import type { APIError } from '@vizit/api';
import { ScoreAnImageDialog } from '@vizit/organisms';
import { useAssetUpload, useProductCategoryList } from './asset-upload-dialog.hooks';

interface AssetUploadDialogProps {
  /**
   * The ID of the organization to use for the upload
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * The ID of the portfolio to use for the upload
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * The function to call when the image is uploaded
   *
   * @param id - The ID of the image
   */
  onSuccessfulUpload: (id: string) => void;
  /**
   * The function to call when the image upload fails
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: unknown | APIError) => void;
}

function AssetUploadDialog(props: AssetUploadDialogProps) {
  const {
    organizationId,
    portfolioId,
    isOpen,
    onOpenChange,
    hideTrigger = false,
    useIcon = false,
    onSuccessfulUpload,
    onAuthenticationError,
  } = props;

  const productCategoryComboBoxProps = useProductCategoryList({ organizationId, portfolioId, onAuthenticationError });
  const { uploadStatus, onSubmit, onFileStatusChange } = useAssetUpload({
    organizationId,
    portfolioId,
    onSuccessfulUpload,
    onAuthenticationError,
  });

  return (
    <ScoreAnImageDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideTrigger={hideTrigger}
      uploadStatus={uploadStatus}
      productCategoryComboBoxProps={productCategoryComboBoxProps}
      useIcon={useIcon}
      onFileStatusChange={onFileStatusChange}
      onSubmit={onSubmit}
    />
  );
}

export { AssetUploadDialog };
